import axios from 'axios';
import { getApiBaseUrl } from '../../core/config/api';

export interface Session {
  id: string;
  created_at: string;
  expires_at: string;
  last_activity: string;
}

export interface SessionResponse {
  success: boolean;
  session?: Session;
  sessions?: Session[];
  user?: any;
  error?: string;
  message?: string;
  deleted_count?: number;
}

class SessionService {
  private static instance: SessionService;

  private constructor() {}

  public static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService();
    }
    return SessionService.instance;
  }

  /**
   * Récupère le token d'authentification depuis le localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Récupère la session actuelle de l'utilisateur
   */
  async getCurrentSession(): Promise<SessionResponse> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Non authentifié' };
      }

      const response = await axios.get<SessionResponse>(
        `${getApiBaseUrl(true)}/sessions/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Erreur lors de la récupération de la session',
        };
      }
      return {
        success: false,
        error: 'Impossible de contacter le serveur',
      };
    }
  }

  /**
   * Récupère toutes les sessions actives d'un utilisateur
   */
  async getUserSessions(userId: number): Promise<SessionResponse> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Non authentifié' };
      }

      const response = await axios.get<SessionResponse>(
        `${getApiBaseUrl(true)}/sessions/user/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Erreur lors de la récupération des sessions',
        };
      }
      return {
        success: false,
        error: 'Impossible de contacter le serveur',
      };
    }
  }

  /**
   * Déconnecte l'utilisateur (supprime la session actuelle)
   */
  async logout(): Promise<SessionResponse> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Non authentifié' };
      }

      const response = await axios.post<SessionResponse>(
        `${getApiBaseUrl(true)}/sessions/logout`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      // Supprimer le token du localStorage après déconnexion réussie
      if (response.data.success) {
        localStorage.removeItem('token');
      }

      return response.data;
    } catch (error: any) {
      // Supprimer le token même en cas d'erreur
      localStorage.removeItem('token');
      
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Erreur lors de la déconnexion',
        };
      }
      return {
        success: false,
        error: 'Impossible de contacter le serveur',
      };
    }
  }

  /**
   * Déconnecte l'utilisateur de toutes ses sessions
   */
  async logoutAll(): Promise<SessionResponse> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Non authentifié' };
      }

      const response = await axios.post<SessionResponse>(
        `${getApiBaseUrl(true)}/sessions/logout-all`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      // Supprimer le token du localStorage après déconnexion réussie
      if (response.data.success) {
        localStorage.removeItem('token');
      }

      return response.data;
    } catch (error: any) {
      // Supprimer le token même en cas d'erreur
      localStorage.removeItem('token');
      
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Erreur lors de la déconnexion',
        };
      }
      return {
        success: false,
        error: 'Impossible de contacter le serveur',
      };
    }
  }

  /**
   * Supprime une session spécifique
   */
  async deleteSession(sessionId: string): Promise<SessionResponse> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Non authentifié' };
      }

      const response = await axios.delete<SessionResponse>(
        `${getApiBaseUrl(true)}/sessions/${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Erreur lors de la suppression de la session',
        };
      }
      return {
        success: false,
        error: 'Impossible de contacter le serveur',
      };
    }
  }

  /**
   * Prolonge la session actuelle
   */
  async extendSession(additionalHours: number = 24): Promise<SessionResponse> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Non authentifié' };
      }

      const response = await axios.post<SessionResponse>(
        `${getApiBaseUrl(true)}/sessions/extend`,
        { additional_hours: additionalHours },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.error || 'Erreur lors de la prolongation de la session',
        };
      }
      return {
        success: false,
        error: 'Impossible de contacter le serveur',
      };
    }
  }
}

export default SessionService;

